import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useCallback, useMemo, useState } from 'react';
import * as Sentry from '@sentry/react';
import cn from 'classnames';
import { Controller, useForm } from 'react-hook-form';
import { BiContainer, Box, Button, Column, FullPageModal, Row, Tabs, TextButton, Typography, useDevice, useOpenUnsavedChangesModal } from 'seagull';
import { Undo } from 'seagull/icons/arrows';
import { Checkmark, CloseCross } from 'seagull/icons/essentials';
import { useStore } from '~/contexts';
import { useLocaleKeys } from '~/i18n';
import { NotificationSettingsEntryType } from '~/models/NotificationSettingsModel';
import { selectors } from '~/store/state-selectors';
import { useRevertNotificationSetting } from '../../hooks/useRevertNotificationSetting';
import { useUpdateNotificationSettingsV2 } from '../../hooks/useUpdateNotificationSettingsV2';
import { deriveIsActive, Tab } from '../../NotificationSettings.utils';
import { NotificationEmailPreview } from '../NotificationEmailPreview/NotificationEmailPreview';
import { SMS_VARIANT_KEY } from '../NotificationEmailPreview/NotificationEmailPreview.utils';
import { NotificationSmsPreview } from '../NotificationSmsPreview/NotificationSmsPreview';
import { NotificationEditField } from './components/NotificationEditField';
import { NotifyRow } from './components/NotifyRow';
import styles from './NotificationEditModal.module.scss';
import { GetNotificationEditModalTID } from './NotificationEditModal.testIds';
import { isDraftValid, validateDraft, SUBJECT_MAX_LENGTH } from './NotificationEditModal.utils';
const resolveErrorText = (errors, code)=>{
    if (code == null) return undefined;
    switch(code){
        case 'NOTIFICATION_V2_SUBJECT_EMPTY':
            return errors.NOTIFICATION_V2_SUBJECT_EMPTY();
        case 'NOTIFICATION_V2_SUBJECT_NEWLINE':
            return errors.NOTIFICATION_V2_SUBJECT_NEWLINE();
        case 'NOTIFICATION_V2_SUBJECT_TOO_LONG':
            return errors.NOTIFICATION_V2_SUBJECT_TOO_LONG({
                limit: SUBJECT_MAX_LENGTH
            });
        case 'NOTIFICATION_V2_MESSAGE_EMPTY':
            return errors.NOTIFICATION_V2_MESSAGE_EMPTY();
        case 'NOTIFICATION_V2_RECIPIENTS_EMPTY':
            return errors.NOTIFICATION_V2_RECIPIENTS_EMPTY();
        case 'NOTIFICATION_V2_CONTENT_NOT_EDITABLE':
            return errors.NOTIFICATION_V2_CONTENT_NOT_EDITABLE();
        case 'NOTIFICATION_V2_RECIPIENTS_NOT_EDITABLE':
            return errors.NOTIFICATION_V2_RECIPIENTS_NOT_EDITABLE();
        case 'NOTIFICATION_V2_MISSING_REQUIRED_TAG':
            return errors.NOTIFICATION_V2_MISSING_REQUIRED_TAG();
        default:
            return errors.default();
    }
};
const EMPTY_VARIANT = {
    subject: '',
    message: '',
    templateVariables: {}
};
const EMPTY_VARIANTS = {};
const UNKNOWN_TEMPLATE_VARIABLES_PREFIX = 'NOTIFICATION_V2_UNKNOWN_TEMPLATE_VARIABLES:';
// JSON-encoded so template-variable labels containing commas survive the round-trip through
// react-hook-form's single-string `root.message`.
const decodeUnknownTemplateVariables = (rootMessage)=>{
    if (rootMessage == null || !rootMessage.startsWith(UNKNOWN_TEMPLATE_VARIABLES_PREFIX)) return [];
    try {
        const parsed = JSON.parse(rootMessage.slice(UNKNOWN_TEMPLATE_VARIABLES_PREFIX.length));
        return Array.isArray(parsed) ? parsed : [];
    } catch (unused) {
        return [];
    }
};
const VARIANT_LABEL_OVERRIDES = {
    invoiceIssuedAutoProcess: {
        standard: (t)=>t.byNotification.invoiceIssuedAutoProcess.standard()
    },
    invoiceIssuedRequiresApproval: {
        auto: (t)=>t.byNotification.invoiceIssuedRequiresApproval.auto(),
        manual: (t)=>t.byNotification.invoiceIssuedRequiresApproval.manual()
    },
    payoutDepositedVendor: {
        standard: (t)=>t.byNotification.payoutDepositedVendor.standard(),
        withdrawal: (t)=>t.byNotification.payoutDepositedVendor.withdrawal()
    },
    payoutFailedVendor: {
        standard: (t)=>t.byNotification.payoutFailedVendor.standard(),
        withdrawal: (t)=>t.byNotification.payoutFailedVendor.withdrawal()
    },
    relationshipUpdatedWithChanges: {
        standard: (t)=>t.byNotification.relationshipUpdatedWithChanges.standard()
    }
};
const VARIANT_LABEL_PATH_PREFIX = 'pages.notificationSettings.contentCustomization.variantLabels.';
const spacedKey = (key)=>{
    const spaced = key.replace(/([A-Z])/g, ' $1').trim();
    return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
};
const resolveVariantLabel = (notificationName, key, variantLabels)=>{
    var _VARIANT_LABEL_OVERRIDES_notificationName;
    const override = (_VARIANT_LABEL_OVERRIDES_notificationName = VARIANT_LABEL_OVERRIDES[notificationName]) === null || _VARIANT_LABEL_OVERRIDES_notificationName === void 0 ? void 0 : _VARIANT_LABEL_OVERRIDES_notificationName[key];
    if (override != null) {
        const overrideValue = override(variantLabels);
        // Leaked path on a declared override → humanize key, never fall back to the generic map (auto/manual exist there with unrelated descriptors).
        return overrideValue.startsWith(VARIANT_LABEL_PATH_PREFIX) ? spacedKey(key) : overrideValue;
    }
    const localized = variantLabels[key];
    if (typeof localized === 'function') {
        const value = localized();
        if (!value.startsWith(VARIANT_LABEL_PATH_PREFIX)) return value;
    }
    return spacedKey(key);
};
const seedVariants = (source, keys)=>{
    const seeded = {};
    for (const key of keys){
        var _source_key;
        seeded[key] = (_source_key = source[key]) !== null && _source_key !== void 0 ? _source_key : EMPTY_VARIANT;
    }
    return seeded;
};
const deriveVariantKeys = (source)=>{
    const keys = Object.keys(source);
    return keys.length > 0 ? keys : [
        'standard'
    ];
};
export const NotificationEditModal = (param)=>{
    let { notificationName, notification, notificationLabel, selectedTab, customTemplateVariables = [], onClose, onRevertSuccess, testId = 'notification-edit-modal' } = param;
    var _notification_variants, _ref, _ref1, _form_watch, _form_watch1, _ref2, _resolveErrorText, _ref3, _ref4;
    var _form_formState_errors_variants, _variantFieldErrors_subject, _variantFieldErrors_message, _form_formState_errors_recipientTypes, _variantFieldErrors_root, _notification_variants_activeVariantKey, _notification_variants_activeVariantKey1;
    const { NotificationEditModalTestIds: testIds } = useMemo(()=>GetNotificationEditModalTID(testId), [
        testId
    ]);
    const { isMobileDevice } = useDevice();
    const { t: contentTexts } = useLocaleKeys('pages.notificationSettings.contentCustomization');
    const { t: pageTexts } = useLocaleKeys('pages.notificationSettings');
    const businessId = useStore(selectors.user.businessId);
    const businessLogoUrl = useStore(selectors.user.businessLogo);
    const businessName = useStore(selectors.user.businessName);
    const branding = useMemo(()=>selectedTab === Tab.Client ? {
            type: 'business',
            name: businessName !== null && businessName !== void 0 ? businessName : '',
            logoUrl: businessLogoUrl !== null && businessLogoUrl !== void 0 ? businessLogoUrl : undefined
        } : {
            type: 'anchor'
        }, [
        selectedTab,
        businessName,
        businessLogoUrl
    ]);
    const initialVariants = (_notification_variants = notification.variants) !== null && _notification_variants !== void 0 ? _notification_variants : EMPTY_VARIANTS;
    const [variantKeys, setVariantKeys] = useState(()=>deriveVariantKeys(initialVariants));
    const initialVariantKey = variantKeys[0];
    const [activeVariantKey, setActiveVariantKey] = useState(initialVariantKey);
    const activeVariantIsSms = activeVariantKey.toLowerCase() === SMS_VARIANT_KEY;
    const [hasVendorOverrides, setHasVendorOverrides] = useState(notification.hasVendorOverrides);
    const [isRefreshingFromRevert, setIsRefreshingFromRevert] = useState(false);
    const initialRecurring = notification.type === NotificationSettingsEntryType.Recurring ? notification.recurring : null;
    const initialMinutes = (_ref = initialRecurring === null || initialRecurring === void 0 ? void 0 : initialRecurring.minutes) !== null && _ref !== void 0 ? _ref : null;
    const initialTimes = (_ref1 = initialRecurring === null || initialRecurring === void 0 ? void 0 : initialRecurring.times) !== null && _ref1 !== void 0 ? _ref1 : null;
    const customTemplateVariableNames = useMemo(()=>customTemplateVariables.map((variable)=>variable.label), [
        customTemplateVariables
    ]);
    const allowedSystemKeysFor = useCallback((key)=>{
        var _ref;
        var _notification_variants_key;
        return new Set(Object.keys((_ref = (_notification_variants_key = notification.variants[key]) === null || _notification_variants_key === void 0 ? void 0 : _notification_variants_key.templateVariables) !== null && _ref !== void 0 ? _ref : {}));
    }, [
        notification.variants
    ]);
    const resolver = useCallback((values)=>{
        const variantErrors = {};
        for (const key of variantKeys){
            var _values_variants_key;
            const variant = (_values_variants_key = values.variants[key]) !== null && _values_variants_key !== void 0 ? _values_variants_key : EMPTY_VARIANT;
            const skipSubject = key.toLowerCase() === SMS_VARIANT_KEY;
            const draftErrors = validateDraft(variant.subject, variant.message, allowedSystemKeysFor(key), customTemplateVariableNames, {
                skipSubject
            });
            if (isDraftValid(draftErrors)) continue;
            const entry = {};
            if (draftErrors.subject != null) {
                entry.subject = {
                    type: 'validate',
                    message: draftErrors.subject
                };
            }
            if (draftErrors.message != null) {
                entry.message = {
                    type: 'validate',
                    message: draftErrors.message
                };
            }
            if (draftErrors.unknownTemplateVariables != null && draftErrors.unknownTemplateVariables.length > 0) {
                entry.root = {
                    type: 'validate',
                    message: "".concat(UNKNOWN_TEMPLATE_VARIABLES_PREFIX).concat(JSON.stringify(draftErrors.unknownTemplateVariables))
                };
            }
            variantErrors[key] = entry;
        }
        const fieldErrors = {};
        if (Object.keys(variantErrors).length > 0) {
            fieldErrors.variants = variantErrors;
        }
        const requiresRecipients = selectedTab === Tab.Vendor && notification.editability.recipients;
        if (requiresRecipients && values.recipientTypes.length === 0) {
            fieldErrors.recipientTypes = {
                type: 'validate',
                message: 'NOTIFICATION_V2_RECIPIENTS_EMPTY'
            };
        }
        const hasErrors = Object.keys(fieldErrors).length > 0;
        return {
            values: hasErrors ? {} : values,
            errors: fieldErrors
        };
    }, [
        variantKeys,
        selectedTab,
        notification.editability.recipients,
        allowedSystemKeysFor,
        customTemplateVariableNames
    ]);
    const form = useForm({
        mode: 'onSubmit',
        resolver,
        defaultValues: {
            variants: seedVariants(initialVariants, variantKeys),
            recipientTypes: notification.recipientTypes,
            minutes: initialMinutes,
            times: initialTimes
        }
    });
    const subjectFieldName = "variants.".concat(activeVariantKey, ".subject");
    const messageFieldName = "variants.".concat(activeVariantKey, ".message");
    const subjectValue = (_form_watch = form.watch(subjectFieldName)) !== null && _form_watch !== void 0 ? _form_watch : '';
    const messageValue = (_form_watch1 = form.watch(messageFieldName)) !== null && _form_watch1 !== void 0 ? _form_watch1 : '';
    const watchedRecipientTypes = form.watch('recipientTypes');
    const watchedMinutes = form.watch('minutes');
    const watchedTimes = form.watch('times');
    // RHF only tracks isDirty/isSubmitted if subscribed via render-time read.
    const { isDirty, isSubmitted } = form.formState;
    const pickerAllowedSystemKeys = useMemo(()=>allowedSystemKeysFor(activeVariantKey), [
        allowedSystemKeysFor,
        activeVariantKey
    ]);
    const openUnsavedChangesModal = useOpenUnsavedChangesModal();
    const { update, isLoading: isSaving, error: saveError } = useUpdateNotificationSettingsV2({
        onSuccess: ()=>onClose()
    });
    const resetEditorFromNotification = (fresh)=>{
        var _fresh_variants, _ref, _ref1;
        const freshVariants = (_fresh_variants = fresh.variants) !== null && _fresh_variants !== void 0 ? _fresh_variants : EMPTY_VARIANTS;
        const freshKeys = deriveVariantKeys(freshVariants);
        const freshRecurring = fresh.type === NotificationSettingsEntryType.Recurring ? fresh.recurring : null;
        const freshMinutes = (_ref = freshRecurring === null || freshRecurring === void 0 ? void 0 : freshRecurring.minutes) !== null && _ref !== void 0 ? _ref : null;
        const freshTimes = (_ref1 = freshRecurring === null || freshRecurring === void 0 ? void 0 : freshRecurring.times) !== null && _ref1 !== void 0 ? _ref1 : null;
        setVariantKeys(freshKeys);
        if (!freshKeys.includes(activeVariantKey)) setActiveVariantKey(freshKeys[0]);
        setHasVendorOverrides(fresh.hasVendorOverrides);
        form.reset({
            variants: seedVariants(freshVariants, freshKeys),
            recipientTypes: fresh.recipientTypes,
            minutes: freshMinutes,
            times: freshTimes
        });
    };
    const handleRevertSuccess = async ()=>{
        if (onRevertSuccess == null) return;
        setIsRefreshingFromRevert(true);
        try {
            const fresh = await onRevertSuccess();
            // The override was cleared on the server but we have no fresh state to populate the
            // editor with. Bail out to the parent so it can re-fetch, rather than leaving the
            // user looking at stale overrides with the Revert button still visible.
            if (fresh == null) onClose();
            else resetEditorFromNotification(fresh);
        } catch (error) {
            Sentry.captureException(error, {
                tags: {
                    source: 'notification-edit-modal-revert-refresh'
                }
            });
            onClose();
        } finally{
            setIsRefreshingFromRevert(false);
        }
    };
    const { revert, isLoading: isReverting } = useRevertNotificationSetting({
        onSuccess: ()=>{
            void handleRevertSuccess();
        }
    });
    const onValid = (values)=>{
        if (businessId == null) return;
        const variantPayload = {};
        for (const key of variantKeys){
            var _values_variants_key;
            const variant = (_values_variants_key = values.variants[key]) !== null && _values_variants_key !== void 0 ? _values_variants_key : EMPTY_VARIANT;
            variantPayload[key] = {
                subject: variant.subject.trim(),
                message: variant.message.trim(),
                templateVariables: variant.templateVariables
            };
        }
        // Modal saves never touch the toggle state — that lives behind a separate endpoint —
        // so editing recipients (or content) here cannot accidentally flip an off toggle on.
        const baseUpdate = notification.type === NotificationSettingsEntryType.Recurring ? {
            type: NotificationSettingsEntryType.Recurring,
            recipientTypes: values.recipientTypes,
            recurring: {
                minutes: values.minutes,
                times: values.times
            }
        } : {
            type: NotificationSettingsEntryType.Single,
            recipientTypes: values.recipientTypes
        };
        update({
            businessId,
            notificationName,
            data: _object_spread_props(_object_spread({}, baseUpdate), {
                variants: variantPayload
            })
        });
    };
    const onInvalid = (errs)=>{
        const firstBadVariantKey = variantKeys.find((k)=>{
            var _errs_variants;
            return ((_errs_variants = errs.variants) === null || _errs_variants === void 0 ? void 0 : _errs_variants[k]) != null;
        });
        if (firstBadVariantKey != null && firstBadVariantKey !== activeVariantKey) {
            setActiveVariantKey(firstBadVariantKey);
        }
    };
    const handleSave = form.handleSubmit(onValid, onInvalid);
    const handleRevert = async ()=>{
        if (!canRevert) return;
        if (businessId == null) return;
        const confirmed = await openUnsavedChangesModal({
            title: contentTexts.revertConfirmTitle(),
            content: contentTexts.revertConfirmMessage(),
            primaryButtonLabel: contentTexts.revertConfirmConfirm(),
            secondaryButtonLabel: contentTexts.revertConfirmCancel()
        });
        if (!confirmed) return;
        revert({
            businessId,
            notificationName
        });
    };
    const handleCancel = async ()=>{
        if (isDirty) {
            const confirmed = await openUnsavedChangesModal({
                title: contentTexts.discardChangesTitle(),
                content: contentTexts.discardChangesMessage(),
                primaryButtonLabel: contentTexts.discardChangesConfirm(),
                secondaryButtonLabel: contentTexts.discardChangesKeep()
            });
            if (!confirmed) return;
        }
        onClose();
    };
    // Since ANCR-908 recipients survive toggle-off, so Active requires toggle on AND at least one recipient.
    const isActive = notification.editability.recipients === false || deriveIsActive(notification) && watchedRecipientTypes.length > 0;
    const canRevert = hasVendorOverrides;
    const contentEditable = notification.editability.content;
    const variantFieldErrors = (_form_formState_errors_variants = form.formState.errors.variants) === null || _form_formState_errors_variants === void 0 ? void 0 : _form_formState_errors_variants[activeVariantKey];
    const subjectErrorCode = variantFieldErrors === null || variantFieldErrors === void 0 ? void 0 : (_variantFieldErrors_subject = variantFieldErrors.subject) === null || _variantFieldErrors_subject === void 0 ? void 0 : _variantFieldErrors_subject.message;
    const messageErrorCode = variantFieldErrors === null || variantFieldErrors === void 0 ? void 0 : (_variantFieldErrors_message = variantFieldErrors.message) === null || _variantFieldErrors_message === void 0 ? void 0 : _variantFieldErrors_message.message;
    const recipientsErrorCode = (_form_formState_errors_recipientTypes = form.formState.errors.recipientTypes) === null || _form_formState_errors_recipientTypes === void 0 ? void 0 : _form_formState_errors_recipientTypes.message;
    const saveErrorCode = saveError === null || saveError === void 0 ? void 0 : saveError.code;
    const inlineErrorKey = (_ref2 = subjectErrorCode !== null && subjectErrorCode !== void 0 ? subjectErrorCode : messageErrorCode) !== null && _ref2 !== void 0 ? _ref2 : saveErrorCode;
    const variantUnknownTemplateVariables = decodeUnknownTemplateVariables(variantFieldErrors === null || variantFieldErrors === void 0 ? void 0 : (_variantFieldErrors_root = variantFieldErrors.root) === null || _variantFieldErrors_root === void 0 ? void 0 : _variantFieldErrors_root.message);
    return /*#__PURE__*/ _jsxs(FullPageModal, {
        contentSkin: "light",
        contentClassName: styles.modalContent,
        headerClassName: styles.modalHeader,
        testId: testId,
        onClose: handleCancel,
        children: [
            /*#__PURE__*/ _jsx(FullPageModal.HeaderStart, {
                children: /*#__PURE__*/ _jsxs(Row, {
                    gap: 8,
                    alignItems: "center",
                    style: {
                        minWidth: 0
                    },
                    children: [
                        /*#__PURE__*/ _jsx(Typography, {
                            variant: "h6",
                            weight: "normal",
                            className: styles.title,
                            testId: testIds.Title(),
                            children: "".concat(contentTexts.modalTitle(), " - ").concat(notificationLabel)
                        }),
                        !isMobileDevice && /*#__PURE__*/ _jsx(Box, {
                            as: "span",
                            inline: true,
                            className: cn(styles.statusBadge, isActive ? styles.active : styles.inactive),
                            testId: testIds.StatusBadge(),
                            children: isActive ? pageTexts.statusActive() : pageTexts.statusInactive()
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsxs(FullPageModal.MainActions, {
                children: [
                    isMobileDevice ? /*#__PURE__*/ _jsx(Button, {
                        skin: "darkSecondary",
                        size: "iconOnly",
                        loading: isReverting || isRefreshingFromRevert,
                        disabled: !canRevert,
                        tooltip: {
                            title: canRevert ? contentTexts.revertButton() : contentTexts.revertButtonDisabledTooltip()
                        },
                        onClick: handleRevert,
                        bi: {
                            name: 'revert_to_default'
                        },
                        testId: testIds.RevertButton(),
                        children: /*#__PURE__*/ _jsx(Undo, {
                            size: 20
                        })
                    }) : /*#__PURE__*/ _jsx(TextButton, {
                        size: "small",
                        skin: "dark",
                        underline: "none",
                        loading: isReverting || isRefreshingFromRevert,
                        disabled: !canRevert,
                        tooltip: !canRevert ? {
                            title: contentTexts.revertButtonDisabledTooltip()
                        } : undefined,
                        onClick: handleRevert,
                        prefixIcon: /*#__PURE__*/ _jsx(Undo, {
                            size: 20
                        }),
                        bi: {
                            name: 'revert_to_default'
                        },
                        testId: testIds.RevertButton(),
                        className: styles.revertButton,
                        children: contentTexts.revertButton()
                    }),
                    /*#__PURE__*/ _jsx(Button, {
                        skin: "primaryOutline",
                        size: isMobileDevice ? 'iconOnly' : 'small',
                        className: styles.headerCta,
                        onClick: handleCancel,
                        tooltip: isMobileDevice ? {
                            title: contentTexts.cancelButton()
                        } : undefined,
                        bi: {
                            name: 'cancel'
                        },
                        testId: testIds.CancelButton(),
                        children: isMobileDevice ? /*#__PURE__*/ _jsx(CloseCross, {
                            size: 16
                        }) : contentTexts.cancelButton()
                    }),
                    /*#__PURE__*/ _jsx(Button, {
                        skin: "primary",
                        size: isMobileDevice ? 'iconOnly' : 'small',
                        loading: isSaving,
                        className: styles.headerCta,
                        onClick: handleSave,
                        tooltip: isMobileDevice ? {
                            title: contentTexts.saveButton()
                        } : undefined,
                        bi: {
                            name: 'save'
                        },
                        testId: testIds.SaveButton(),
                        children: isMobileDevice ? /*#__PURE__*/ _jsx(Checkmark, {
                            size: 16
                        }) : contentTexts.saveButton()
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs(BiContainer, {
                name: "edit_notification",
                children: [
                    variantKeys.length > 1 && /*#__PURE__*/ _jsx("div", {
                        className: cn(styles.stickyTabs, {
                            [styles.stickyTabsMobile]: isMobileDevice
                        }),
                        "data-testid": testIds.StickyTabsContainer(),
                        children: /*#__PURE__*/ _jsx(Tabs, {
                            bi: {
                                name: 'notification_edit_variant_tabs'
                            },
                            value: activeVariantKey,
                            onChange: (_event, nextValue)=>{
                                setActiveVariantKey(nextValue);
                                form.clearErrors();
                            },
                            testId: testIds.VariantTabs(),
                            tabs: variantKeys.map((key)=>({
                                    id: key,
                                    value: key,
                                    component: resolveVariantLabel(notificationName, key, contentTexts.variantLabels),
                                    testId: testIds.VariantTab(key)
                                }))
                        })
                    }),
                    /*#__PURE__*/ _jsx(Column, {
                        gap: isMobileDevice ? 16 : 24,
                        padding: isMobileDevice ? 16 : 32,
                        testId: testIds.Root(),
                        className: cn(styles.card, {
                            [styles.cardMobile]: isMobileDevice
                        }),
                        children: /*#__PURE__*/ _jsxs("div", {
                            className: cn(styles.bodyGrid, {
                                [styles.bodyGridMobile]: isMobileDevice
                            }),
                            "data-testid": testIds.BodyGrid(),
                            children: [
                                /*#__PURE__*/ _jsxs(Column, {
                                    gap: 16,
                                    children: [
                                        !activeVariantIsSms && /*#__PURE__*/ _jsxs(Column, {
                                            gap: 4,
                                            className: styles.fieldContainer,
                                            testId: testIds.SubjectFieldContainer(),
                                            children: [
                                                /*#__PURE__*/ _jsx(Controller, {
                                                    control: form.control,
                                                    name: subjectFieldName,
                                                    render: (param)=>{
                                                        let { field } = param;
                                                        var _field_value;
                                                        return /*#__PURE__*/ _jsx(NotificationEditField, {
                                                            label: contentTexts.subjectLabel(),
                                                            value: (_field_value = field.value) !== null && _field_value !== void 0 ? _field_value : '',
                                                            onChange: field.onChange,
                                                            placeholder: contentTexts.subjectPlaceholder(),
                                                            error: subjectErrorCode != null,
                                                            disabled: !contentEditable,
                                                            bi: {
                                                                name: 'subject'
                                                            },
                                                            testId: testIds.SubjectField(),
                                                            adornmentTestId: testIds.SubjectVariableAdornment(),
                                                            pickerTestId: testIds.SubjectVariablePicker(),
                                                            customVariables: customTemplateVariableNames,
                                                            allowedSystemKeys: pickerAllowedSystemKeys,
                                                            validateInBottomSheet: (value)=>{
                                                                var _form_getValues;
                                                                const code = validateDraft(value, (_form_getValues = form.getValues(messageFieldName)) !== null && _form_getValues !== void 0 ? _form_getValues : '').subject;
                                                                return {
                                                                    error: code != null,
                                                                    helperText: resolveErrorText(contentTexts.errors, code)
                                                                };
                                                            }
                                                        });
                                                    }
                                                }, subjectFieldName),
                                                subjectErrorCode != null && /*#__PURE__*/ _jsx(Typography, {
                                                    skin: "error",
                                                    variant: "paragraph3",
                                                    testId: testIds.SubjectError(),
                                                    children: resolveErrorText(contentTexts.errors, subjectErrorCode)
                                                })
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsxs(Column, {
                                            gap: 4,
                                            className: styles.fieldContainer,
                                            testId: testIds.MessageFieldContainer(),
                                            children: [
                                                /*#__PURE__*/ _jsx(Controller, {
                                                    control: form.control,
                                                    name: messageFieldName,
                                                    render: (param)=>{
                                                        let { field } = param;
                                                        var _field_value;
                                                        return /*#__PURE__*/ _jsx(NotificationEditField, {
                                                            multiline: true,
                                                            minRows: 6,
                                                            label: contentTexts.messageLabel(),
                                                            value: (_field_value = field.value) !== null && _field_value !== void 0 ? _field_value : '',
                                                            onChange: field.onChange,
                                                            placeholder: contentTexts.messagePlaceholder(),
                                                            error: messageErrorCode != null,
                                                            disabled: !contentEditable,
                                                            bi: {
                                                                name: 'message'
                                                            },
                                                            testId: testIds.MessageField(),
                                                            adornmentTestId: testIds.MessageVariableAdornment(),
                                                            adornmentClassName: styles.messageAdornment,
                                                            pickerTestId: testIds.MessageVariablePicker(),
                                                            customVariables: customTemplateVariableNames,
                                                            allowedSystemKeys: pickerAllowedSystemKeys,
                                                            validateInBottomSheet: (value)=>{
                                                                var _form_getValues;
                                                                const code = validateDraft((_form_getValues = form.getValues(subjectFieldName)) !== null && _form_getValues !== void 0 ? _form_getValues : '', value).message;
                                                                return {
                                                                    error: code != null,
                                                                    helperText: resolveErrorText(contentTexts.errors, code)
                                                                };
                                                            }
                                                        });
                                                    }
                                                }, messageFieldName),
                                                messageErrorCode != null && /*#__PURE__*/ _jsx(Typography, {
                                                    skin: "error",
                                                    variant: "paragraph3",
                                                    testId: testIds.MessageError(),
                                                    children: resolveErrorText(contentTexts.errors, messageErrorCode)
                                                })
                                            ]
                                        }),
                                        variantUnknownTemplateVariables.length > 0 && /*#__PURE__*/ _jsx(Typography, {
                                            skin: "error",
                                            variant: "paragraph3",
                                            testId: testIds.UnknownTemplateVariablesError(),
                                            children: contentTexts.errors.NOTIFICATION_V2_UNKNOWN_TEMPLATE_VARIABLES({
                                                templateVariables: variantUnknownTemplateVariables.map((variable)=>"{{".concat(variable, "}}")).join(', ')
                                            })
                                        }),
                                        inlineErrorKey != null && inlineErrorKey !== subjectErrorCode && inlineErrorKey !== messageErrorCode && /*#__PURE__*/ _jsx(Typography, {
                                            skin: "error",
                                            variant: "paragraph3",
                                            testId: testIds.InlineError(),
                                            children: (_resolveErrorText = resolveErrorText(contentTexts.errors, inlineErrorKey)) !== null && _resolveErrorText !== void 0 ? _resolveErrorText : contentTexts.errors.default()
                                        }),
                                        /*#__PURE__*/ _jsx(NotifyRow, {
                                            selectedTab: selectedTab,
                                            notificationType: notification.type,
                                            notificationName: notificationName,
                                            recipientTypes: watchedRecipientTypes,
                                            recipientTypeOptions: notification.recipientTypeOptions,
                                            recipientsEditable: notification.editability.recipients,
                                            recipientsError: recipientsErrorCode != null,
                                            minutes: watchedMinutes,
                                            times: watchedTimes,
                                            onChangeRecipients: (next)=>{
                                                form.setValue('recipientTypes', next, {
                                                    shouldDirty: true,
                                                    shouldValidate: isSubmitted
                                                });
                                            },
                                            onChangeMinutes: (next)=>{
                                                form.setValue('minutes', next, {
                                                    shouldDirty: true,
                                                    shouldValidate: isSubmitted
                                                });
                                            },
                                            onChangeTimes: (next)=>{
                                                form.setValue('times', next, {
                                                    shouldDirty: true,
                                                    shouldValidate: isSubmitted
                                                });
                                            }
                                        }),
                                        recipientsErrorCode != null && /*#__PURE__*/ _jsx(Typography, {
                                            skin: "error",
                                            variant: "paragraph3",
                                            testId: testIds.RecipientsError(),
                                            children: resolveErrorText(contentTexts.errors, recipientsErrorCode)
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsx(Column, {
                                    gap: 12,
                                    testId: testIds.PreviewRoot(),
                                    children: activeVariantIsSms ? /*#__PURE__*/ _jsx(NotificationSmsPreview, {
                                        message: messageValue,
                                        templateVariables: (_ref3 = (_notification_variants_activeVariantKey = notification.variants[activeVariantKey]) === null || _notification_variants_activeVariantKey === void 0 ? void 0 : _notification_variants_activeVariantKey.templateVariables) !== null && _ref3 !== void 0 ? _ref3 : {},
                                        customTemplateVariables: customTemplateVariables,
                                        vendorBusinessName: businessName !== null && businessName !== void 0 ? businessName : ''
                                    }) : /*#__PURE__*/ _jsx(NotificationEmailPreview, {
                                        notificationName: notificationName,
                                        subject: subjectValue,
                                        message: messageValue,
                                        templateVariables: (_ref4 = (_notification_variants_activeVariantKey1 = notification.variants[activeVariantKey]) === null || _notification_variants_activeVariantKey1 === void 0 ? void 0 : _notification_variants_activeVariantKey1.templateVariables) !== null && _ref4 !== void 0 ? _ref4 : {},
                                        customTemplateVariables: customTemplateVariables,
                                        ctaButtonText: notification.ctaButtonText,
                                        branding: branding,
                                        vendorBusinessName: businessName !== null && businessName !== void 0 ? businessName : ''
                                    })
                                })
                            ]
                        })
                    })
                ]
            })
        ]
    });
};
